import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { badgeClass } from "@/lib/config";
import { Download } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Metric({ label, value, sub }) {
  return (
    <div className="bg-white border border-border rounded-sm p-3">
      <div className="text-[11px] uppercase text-mutedsea">{label}</div>
      <div className="font-serif text-xl text-navy mt-0.5">{value}</div>
      {sub && <div className="text-xs text-mutedsea">{sub}</div>}
    </div>
  );
}

export default function BookOfBusiness() {
  const nav = useNavigate();
  const [f, setF] = useState({ producer: "all", carrier: "all", lob: "all", status: "all", biz: "all", state: "", q: "" });
  const [producers, setProducers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [data, setData] = useState({ items: [], metrics: null, total: 0 });

  useEffect(() => {
    api.get("/lookups/owners").then(({ data }) => setProducers(data.items || [])).catch(() => {});
    api.get("/lookups/carriers").then(({ data }) => setCarriers(data.items || [])).catch(() => {});
  }, []);

  const params = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    return p;
  };
  const load = () => api.get(`/agency/book-of-business?${params().toString()}`).then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [JSON.stringify(f)]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/agency/book-of-business/export.csv?${params().toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "book_of_business.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const m = data.metrics;
  const cols = ["Client", "Policy", "Carrier", "LOB", "Status", "Effective", "Expiration", "Written Premium", "Producer", "Renewal", "Commission"];

  return (
    <div className="p-6" data-testid="book-of-business-page">
      <PageHeader title="Book of Business" subtitle="Client → policy → term → premium → carrier → producer → commission → renewal.">
        <Button variant="outline" size="sm" data-testid="book-export" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </PageHeader>

      {m && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4" data-testid="book-metrics">
          <Metric label="Clients" value={m.total_clients} />
          <Metric label="Active Policies" value={m.active_policies} sub={`${m.total_policies} total`} />
          <Metric label="Written Premium" value={money(m.written_premium)} />
          <Metric label="Upcoming Renewal $" value={money(m.upcoming_renewal_premium)} />
          <Metric label="New Business" value={m.new_business} />
          <Metric label="Renewal Business" value={m.renewal_business} />
          <Metric label="Retention" value={m.retention != null ? `${m.retention}%` : "—"} />
        </div>
      )}

      {m && (m.carrier_concentration?.length || m.lob_mix?.length) ? (
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="bg-white border border-border rounded-sm p-3" data-testid="carrier-concentration">
            <div className="text-xs uppercase text-navy font-semibold mb-2">Carrier Concentration</div>
            {m.carrier_concentration.map((c) => <div key={c.name} className="flex justify-between text-sm py-0.5"><span className="text-slate truncate">{c.name}</span><span className="text-mutedsea">{money(c.premium)} · {c.count}</span></div>)}
          </div>
          <div className="bg-white border border-border rounded-sm p-3" data-testid="lob-mix">
            <div className="text-xs uppercase text-navy font-semibold mb-2">Line of Business Mix</div>
            {m.lob_mix.map((c) => <div key={c.name} className="flex justify-between text-sm py-0.5"><span className="text-slate truncate">{c.name}</span><span className="text-mutedsea">{money(c.premium)} · {c.count}</span></div>)}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="book-filters">
        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search policy #, insured…" className="max-w-xs h-9" data-testid="book-search" />
        <Select value={f.producer} onValueChange={(v) => setF({ ...f, producer: v })}><SelectTrigger className="w-40 h-9" data-testid="book-producer"><SelectValue placeholder="Producer" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Producers</SelectItem>{producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.carrier} onValueChange={(v) => setF({ ...f, carrier: v })}><SelectTrigger className="w-40 h-9" data-testid="book-carrier"><SelectValue placeholder="Carrier" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Carriers</SelectItem>{carriers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="w-36 h-9" data-testid="book-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All Statuses</SelectItem>{["Active", "Cancelled", "Nonrenewal Pending", "Expired"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={f.biz} onValueChange={(v) => setF({ ...f, biz: v })}><SelectTrigger className="w-36 h-9" data-testid="book-biz"><SelectValue placeholder="New/Renewal" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">New & Renewal</SelectItem><SelectItem value="new">New Business</SelectItem></SelectContent></Select>
        <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="State" className="w-24 h-9" data-testid="book-state" />
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{cols.map((c) => <th key={c} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
          <tbody data-testid="book-table">
            {data.items.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-mutedsea">No policies match these filters.</td></tr>}
            {data.items.map((r) => (
              <tr key={r.policy_id} data-testid={`book-row-${r.policy_id}`} className="border-b border-border last:border-0 hover:bg-ice">
                <td className="px-3 py-2"><button onClick={() => r.party_id && nav(`/clients/${r.party_id}`)} className="text-atlantic hover:underline nav-link">{r.client}</button></td>
                <td className="px-3 py-2"><button onClick={() => nav(`/policies/${r.policy_id}`)} className="text-atlantic hover:underline nav-link">{r.policy_number}</button></td>
                <td className="px-3 py-2 whitespace-nowrap">{r.carrier}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.line}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={badgeClass(r.status)}>{r.status}</Badge></td>
                <td className="px-3 py-2 whitespace-nowrap">{r.effective_date}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.expiration_date}</td>
                <td className="px-3 py-2 whitespace-nowrap">{money(r.written_premium)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.producer}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.renewal_status}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.commission ? money(r.commission) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-mutedsea mt-2">{data.total} policies in scope.</p>
    </div>
  );
}
